import { useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, Star } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg } from "../../lib/api";
import { FormGrid } from "./FormField";
import { PayPalSettings } from "./AdminPayPal";
import { COMPANY_TAX } from "./AdminDocuments";
import { AdminH } from "./AdminLayout";
import { WordTemplates } from "./WordTemplates";

const FIELDS = [
  { key: "name", label: "Company name (Denominazione)", full: true }, { key: "vat_number", label: "VAT number" }, { key: "email", label: "Email", type: "email" },
  { key: "address", label: "Address", full: true }, { key: "zip", label: "ZIP / CAP" }, { key: "city", label: "City" }, { key: "country", label: "Country" },
  { key: "iban", label: "IBAN" }, { key: "swift", label: "SWIFT / BIC" },
  { key: "logo", label: "Logo (used on proforma and invoice PDFs)", type: "image", full: true },
  { key: "tax_rate", label: "Tax regime (preselected in every new manual proforma and invoice of this company)", type: "select", required: true, options: { static: COMPANY_TAX } },
  { key: "doc_language", label: "Language of proforma, invoices and credit notes (PDF)", type: "select", required: true, options: { static: [["en", "EN – English"], ["it", "IT – Italiano"], ["es", "ES – Español"]] } },
  { key: "proforma_start", label: "Initial proforma number (starting point for the current year)", type: "number" },
  { key: "invoice_start", label: "Initial invoice number (starting point for the current year)", type: "number" },
  { key: "notes", label: "Notes (printed at the bottom of invoices and proforma)", type: "textarea", rows: 3, full: true },
  { key: "reverse_charge_text", label: "Reverse Charge text (printed on invoices issued with the Reverse Charge mechanism)", type: "textarea", rows: 2, full: true },
  { key: "accountant_email", label: "Accountant email (receives proforma and invoices with the Send to button)", type: "email" },
  { key: "is_default", label: "Default company (used for new events and for events/enrolments without organization)", type: "bool", full: true },
];

const CompanyForm = ({ c, onDone }) => {
  const [f, setF] = useState(c);
  const save = async () => {
    if (!(f.name || "").trim()) return toast.error("Company name is required");
    try {
      if (f.id) await api.put(`/admin/companies/${f.id}`, f);
      else await api.post("/admin/companies", f);
      toast.success("Company saved");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4 rounded-2xl border border-[#00A8B5] bg-cyan-50/40 p-5" data-testid="company-form">
      <FormGrid fields={FIELDS} f={f} setF={setF} prefix="company-" />
      <div className="flex gap-3">
        <button onClick={save} className="btn-buoy !py-2" data-testid="company-save">Save company</button>
        <button onClick={() => onDone(false)} className="text-sm font-semibold text-slate-500" data-testid="company-cancel">Cancel</button>
      </div>
    </div>
  );
};

export const CompaniesManager = () => {
  const { data, reload } = useFetch("/admin/companies");
  const [edit, setEdit] = useState(null);
  const [pp, setPp] = useState(null);
  const [wt, setWt] = useState(null);
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const done = () => { setEdit(null); reload(); };
  const makeDefault = async (c) => {
    try {
      await api.put(`/admin/companies/${c.id}`, { is_default: true });
      toast.success(`${c.name} is now the default company`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="companies-manager">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-display text-lg font-bold text-[#0A2540]"><Building2 size={18} />Companies (event organizers)</p>
        {!edit && <button onClick={() => setEdit({ doc_language: "en", tax_rate: "0" })} className="inline-flex items-center gap-1 text-sm font-semibold text-[#00A8B5]" data-testid="company-add"><Plus size={16} />Add company</button>}
      </div>
      <p className="text-sm text-slate-500">Choose the organizing company in each event (Organization). Its details and IBAN are shown in enrolments, enrolment PDF and enrolment email.</p>
      {edit && !edit.id && <CompanyForm c={edit} onDone={done} />}
      {(data || []).map((c) => (edit?.id === c.id ? <CompanyForm key={c.id} c={edit} onDone={done} /> : (
        <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 p-4 text-sm" data-testid={`company-row-${c.id}`}>
          <p><b>{c.name}</b>{c.is_default && <span className="ml-2 rounded-full bg-[#00A8B5] px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`company-default-badge-${c.id}`}>Default</span>} · VAT {c.vat_number || "-"} · {[c.address, c.zip, c.city, c.country].filter(Boolean).join(", ")} · {c.email}<span className="block font-mono text-xs text-slate-500">IBAN {c.iban || "-"} · SWIFT/BIC {c.swift || "-"}</span>{c.accountant_email && <span className="block text-xs text-slate-600" data-testid={`company-accountant-${c.id}`}>Accountant: {c.accountant_email}</span>}<span className="block text-xs text-slate-600" data-testid={`company-tax-${c.id}`}>Tax regime: <b>{(COMPANY_TAX.find(([v]) => v === String(c.tax_rate ?? "0")) || [, `${c.tax_rate}%`])[1]}</b></span></p>
          <div className="flex items-center gap-2">
            {!c.is_default && <button onClick={() => makeDefault(c)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00A8B5]" data-testid={`company-make-default-${c.id}`}><Star size={14} />Set as default</button>}
            <button onClick={() => setPp(pp === c.id ? null : c.id)} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-[#0A2540]" data-testid={`company-paypal-${c.id}`}>PayPal</button>
            <button onClick={() => setWt(wt === c.id ? null : c.id)} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-[#0A2540]" data-testid={`company-word-${c.id}`}>Word templates</button>
            <button onClick={() => setEdit(c)} className="text-slate-400 hover:text-[#0A2540]" data-testid={`company-edit-${c.id}`}><Pencil size={16} /></button>
            <button onClick={() => del(c)} className="text-slate-400 hover:text-rose-500" data-testid={`company-delete-${c.id}`}><Trash2 size={16} /></button>
          </div>
          {pp === c.id && <div className="basis-full"><PayPalSettings companyId={c.id} /></div>}
          {wt === c.id && <div className="basis-full"><WordTemplates companyId={c.id} /></div>}
        </div>
      )))}
      {data && !data.length && !edit && <p className="text-sm text-slate-500">No companies yet.</p>}
    </div>
  );
};

export default function AdminCompanies() {
  return (
    <div data-testid="admin-companies">
      <AdminH title="Companies" sub="Organizing companies: details, logo, notes, numbering, Reverse Charge, document language, tax, PayPal, default company and Word templates for proforma and invoices." />
      <CompaniesManager />
    </div>
  );
}
